import { DateTime } from 'luxon';

/**
 *  **fixZeroZeroCase**
 *
 * Adjusts the edge case where both start and end times are "00:00" on the same day.
 * In such a case, it shifts the range to be from 00:00 of the same day to 00:00 of the next day.
 *
 * Otherwise, it sets the provided hours and minutes to the given dates.
 *
 * @param fromISO - ISO string representing the start date
 * @param toISO - ISO string representing the end date
 * @param fromHour - Start hour as a string (e.g., "00", "13")
 * @param toHour - End hour as a string
 * @param fromMinute - Start minute as a string, defaults to "00"
 * @param toMinute - End minute as a string, defaults to "00"
 * @returns An object containing Luxon DateTime objects `fromDate` and `toDate` with applied hours/minutes
 *
 *  ISO is a standardized format for date and time, optionally including a time zone offset
 *
 * **getFinalUTCDateRange**
 *
 * Converts a given date range and time values into UTC ISO strings based on the selected time zone.
 * Handles the edge case when both times are "00:00" on the same day.
 *
 * @param range - Object containing:
 *   - `from`: ISO string for the start date
 *   - `to`: ISO string for the end date
 *   - `timeFromHour`: Start hour string
 *   - `timeToHour`: End hour string
 *   - `timeFromMinute`: Start minute string
 *   - `timeToMinute`: End minute string
 * @param selectedZone - time zone string (e.g., "Europe/Belgrade", "America/New_York")
 * @returns An object containing:
 *   - `startDate`: UTC ISO string for the start of the range
 *   - `endDate`: UTC ISO string for the end of the range
 *   Returns `null` for both if `from` or `to` is missing.
 */

interface Range {
  from?: string;
  to?: string;
  timeFromHour?: string;
  timeToHour?: string;
  timeFromMinute?: string;
  timeToMinute?: string;
}

const fixZeroZeroCase = (
  fromISO: string,
  toISO: string,
  fromHour: string,
  toHour: string,
  fromMinute: string = '00',
  toMinute: string = '00',
  fromSecond: string = '00',
  toSecond: string = '00',
  fromMillisecond: string = '00',
  toMillisecond: string = '00',
) => {
  // Convert hour strings to numbers, defaulting to 0 if missing
  const fromHourNum = parseInt(fromHour || '0', 10);
  const toHourNum = parseInt(toHour || '24', 10);

  const fromDate = DateTime.fromISO(fromISO);
  const toDate = DateTime.fromISO(toISO);

  return {
    fromDate: fromDate.set({
      hour: fromHourNum,
      minute: parseInt(fromMinute || '0', 10),
      second: parseInt(fromSecond || '0', 10),
      millisecond: parseInt(fromMillisecond || '0', 10),
    }),
    toDate: toDate.set({
      hour: toHourNum,
      minute: parseInt(toMinute || '0', 10),
      second: parseInt(toSecond || '0', 10),
      millisecond: parseInt(toMillisecond || '0', 10),
    }),
  };
};

export const getFinalUTCDateRange = (range: Range, selectedZone: string) => {
  if (!range.from || !range.to) return { startDate: null, endDate: null };

  const fromHour = range.timeFromHour ?? '00';
  const toHour = range.timeToHour ?? '24';

  const to = new Date(range.to);

  const { fromDate, toDate } = fixZeroZeroCase(range.from, to.toISOString(), fromHour, toHour);

  // convert to UTC based on the selected timezone
  const startDateUTC = fromDate.setZone(selectedZone, { keepLocalTime: true }).toUTC().toISO();
  const endDateUTC = toDate.setZone(selectedZone, { keepLocalTime: true }).toUTC().toISO();

  return { startDate: startDateUTC, endDate: endDateUTC };
};
